import React from 'react';
import { ArrowRight, Shield, Award, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { trustBadges, companyInfo } from '../data/mock';

const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1603788570887-405355e70ca6?w=1920&q=80')`
        }}
      />
      
      {/* Overlay */}
      <div className="absolute inset-0 hero-overlay" />
      
      {/* Content */}
      <div className="container mx-auto px-4 pt-36 pb-24 relative z-10">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-5 py-2.5 mb-10">
            <Shield className="w-4 h-4 text-[#B8956B]" />
            <span className="text-white/90 text-sm font-medium tracking-wide">IS 17900:2022 Certified Installation</span>
          </div>
          
          {/* Headline */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-semibold text-white mb-8 leading-[1.1] heading-display">
            Customized Elevators{' '}
            <span className="text-gold-gradient italic">Engineered</span>
            <br />for Safety, Comfort & Style
          </h1>
          
          {/* Subtext */}
          <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl leading-relaxed font-light">
            {companyInfo.tagline}. Premium lift installation as per IS 17900:2022 standards with 
            30+ years of engineering excellence across Kerala.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-20">
            <Button 
              size="lg"
              onClick={() => scrollToSection('contact')}
              className="btn-gold text-white font-medium px-8 py-6 text-base border-0 group tracking-wide"
            >
              Get a Free Site Consultation
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => scrollToSection('products')}
              className="bg-transparent border border-white/30 text-white hover:bg-white hover:text-[#252A32] px-8 py-6 text-base transition-all tracking-wide"
            >
              View Our Elevator Solutions
            </Button>
          </div>
          
          {/* Trust Badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {trustBadges.map((badge, index) => (
              <div 
                key={badge.id}
                className="glass-effect rounded-xl p-4 border border-white/10 transition-transform hover:scale-[1.02]"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#B8956B]/15 flex items-center justify-center">
                    {badge.icon === 'Shield' && <Shield className="w-5 h-5 text-[#B8956B]" />}
                    {badge.icon === 'Award' && <Award className="w-5 h-5 text-[#B8956B]" />}
                    {badge.icon === 'Wrench' && <CheckCircle className="w-5 h-5 text-[#B8956B]" />}
                    {badge.icon === 'Clock' && <CheckCircle className="w-5 h-5 text-[#B8956B]" />}
                  </div>
                  <div>
                    <p className="text-[#252A32] text-sm font-semibold">{badge.title}</p>
                    <p className="text-[#404752] text-xs">{badge.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10">
        <div className="w-7 h-11 border border-white/25 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-2.5 bg-[#B8956B] rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
